import { Head } from '@inertiajs/react';
import {
    Bike,
    Car,
    Clock,
    HeartPulse,
    PersonStanding,
    ShieldCheck,
} from 'lucide-react';

import { Btn } from '@/Components/Btn';
import { Chip } from '@/Components/Chip';
import { Footer } from '@/Components/Footer';
import { Header } from '@/Components/Header';
import { Headshot } from '@/Components/Headshot';
import { LawyerCardC } from '@/Components/LawyerCardC';
import { LawyerCardH } from '@/Components/LawyerCardH';
import { LawyerCardV } from '@/Components/LawyerCardV';
import { MapStandin } from '@/Components/MapStandin';
import { MobileCTABar } from '@/Components/MobileCTABar';
import { MobileHeader } from '@/Components/MobileHeader';
import { PhoneCTA } from '@/Components/PhoneCTA';
import { Pill } from '@/Components/Pill';
import { SectionHeader } from '@/Components/SectionHeader';
import { Stars } from '@/Components/Stars';
import { TrustBadge } from '@/Components/TrustBadge';
import { UtilityBar } from '@/Components/UtilityBar';
import { Wordmark } from '@/Components/Wordmark';
import { LAWYER_FIXTURES } from '@/__fixtures__/lawyers';

/**
 * Dev-only showcase page for the Ink & Ember component library.
 * Gated server-side by App\Http\Middleware\EnsureDevEnv.
 */

const PHONE = '1-833-INJURY-1';

function Block({
    id,
    title,
    children,
}: {
    id: string;
    title: string;
    children: React.ReactNode;
}) {
    return (
        <section
            id={id}
            aria-labelledby={`${id}-h`}
            className="border-t border-rule py-8"
        >
            <h2 id={`${id}-h`} className="text-[22px] mb-4 text-ink">
                {title}
            </h2>
            <div className="flex flex-wrap items-start gap-4">{children}</div>
        </section>
    );
}

export default function DevComponents() {
    const [a, b, c, d] = LAWYER_FIXTURES;
    if (a === undefined || b === undefined || c === undefined || d === undefined) {
        throw new Error('LAWYER_FIXTURES needs at least 4 entries');
    }

    return (
        <>
            <Head title="Component library — dev" />
            <UtilityBar phone={PHONE} />
            <Header phone={PHONE} active="practice-areas" />
            <MobileHeader phone={PHONE} />

            <main className="max-w-6xl mx-auto px-6 py-10">
                <header className="mb-10">
                    <div className="text-[12px] uppercase tracking-widest text-ember mb-2">
                        Design system · Phase 5
                    </div>
                    <h1 className="text-[48px] leading-tight">
                        Ink &amp; Ember component library
                    </h1>
                    <p className="text-ink-soft text-[18px] mt-2 max-w-2xl">
                        Atoms, page chrome, lawyer cards, and the Mapbox
                        standin. Cards and the map land in Phase 5b; real
                        Mapbox arrives in Phase 9.
                    </p>
                </header>

                <Block id="buttons" title="Btn — sizes × kinds × states">
                    <Btn size="sm">Small</Btn>
                    <Btn size="md">Medium</Btn>
                    <Btn size="lg">Large</Btn>
                    <Btn size="xl">Extra large</Btn>
                    <Btn kind="secondary">Secondary</Btn>
                    <Btn kind="ghost">Ghost</Btn>
                    <Btn kind="danger">Danger</Btn>
                    <Btn loading>Loading</Btn>
                    <Btn disabled>Disabled</Btn>
                    <Btn full>Full width</Btn>
                </Block>

                <Block id="stars" title="Stars — fractional ratings">
                    <Stars value={5} reviews={42} />
                    <Stars value={4.5} reviews={128} />
                    <Stars value={3.2} reviews={9} size={18} />
                    <Stars value={0} />
                </Block>

                <Block id="headshot" title="Headshot — initials placeholder">
                    <Headshot initials="MS" />
                    <Headshot initials="JR" size={64} hue={120} />
                    <Headshot initials="DJ" size={48} hue={240} radius={999} />
                </Block>

                <Block id="chips" title="Chip — filter toggles">
                    <Chip label="Car accident" icon={<Car size={14} />} />
                    <Chip
                        label="Bike"
                        icon={<Bike size={14} />}
                        count={284}
                    />
                    <Chip label="Free consult" selected />
                    <Chip
                        label="Medical malpractice"
                        icon={<HeartPulse size={14} />}
                        tone="ember"
                        selected
                    />
                    <Chip
                        label="Slip & fall"
                        icon={<PersonStanding size={14} />}
                        tone="sage"
                    />
                </Block>

                <Block id="pills" title="Pill — static labels">
                    <Pill tone="ink">Verified</Pill>
                    <Pill tone="ember">Sponsored</Pill>
                    <Pill tone="sage">Free consult</Pill>
                    <Pill tone="gold">Top rated</Pill>
                    <Pill tone="muted">Inactive</Pill>
                </Block>

                <Block id="wordmark" title="Wordmark">
                    <Wordmark size={20} />
                    <Wordmark size={28} sub="Personal injury directory" />
                </Block>

                <Block id="trust" title="TrustBadge">
                    <TrustBadge
                        icon={<ShieldCheck size={16} aria-hidden="true" />}
                        label="Bar-verified"
                    />
                    <TrustBadge
                        icon={<Clock size={16} aria-hidden="true" />}
                        label="Avg response 12 min"
                        tone="ember"
                    />
                </Block>

                <Block id="section-header" title="SectionHeader">
                    <SectionHeader
                        eyebrow="Practice areas"
                        title="Find a lawyer for your injury"
                        sub="Browse by case type and connect with a verified attorney near you."
                        action={<Btn kind="ghost">All practice areas</Btn>}
                    />
                </Block>

                <Block id="phone-cta" title="PhoneCTA">
                    <PhoneCTA number={PHONE} size="sm" />
                    <PhoneCTA number={PHONE} size="md" />
                    <PhoneCTA number={PHONE} size="lg" />
                    <PhoneCTA number={PHONE} size="xl" />
                    <PhoneCTA number={PHONE} kind="secondary" />
                    <PhoneCTA number={PHONE} kind="ghost" />
                </Block>

                <Block id="cards-v" title="LawyerCardV — vertical tile">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
                        <LawyerCardV data={a} />
                        <LawyerCardV data={b} />
                        <LawyerCardV data={c} />
                    </div>
                </Block>

                <Block id="cards-h" title="LawyerCardH — horizontal result row">
                    <div className="flex flex-col gap-3 w-full">
                        <LawyerCardH data={a} />
                        <LawyerCardH data={d} />
                    </div>
                </Block>

                <Block id="cards-c" title="LawyerCardC — compact row">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full">
                        <LawyerCardC data={a} />
                        <LawyerCardC data={b} />
                        <LawyerCardC data={c} />
                        <LawyerCardC data={d} />
                    </div>
                </Block>

                <Block id="map" title="MapStandin — Mapbox placeholder">
                    <div className="w-full">
                        <MapStandin
                            height={360}
                            label="Lawyers near Los Angeles"
                            pins={[
                                { x: 22, y: 38, label: '12 lawyers' },
                                { x: 48, y: 56, label: '8 lawyers', selected: true },
                                { x: 71, y: 30, label: '5 lawyers' },
                                { x: 60, y: 78, label: '3 lawyers' },
                            ]}
                        />
                    </div>
                </Block>
            </main>

            <Footer />
            <MobileCTABar
                phone={PHONE}
                filterCount={3}
                onFilterClick={() => {}}
            />
        </>
    );
}
